import React from 'react';
import ExtractCtrl from '../svc/ExtractCtrl';
import {buildColsMeta, computeSurface, expandGeom} from '../utils/dataUtils';
import {SOURCES_CONFIG} from '../constants/sources';

/**
 * ⚠️  Passer à `false` avant de déployer.
 * En mode dev, charge uniquement le premier lot (1 enregistrement) par source.
 */
const DEV_LIMIT = false;

/**
 * Hook gérant l'intégralité du cycle de vie du chargement des données :
 * appels paginés aux API, fusion des sources par adresse, et état de progression.
 *
 * @param {Object} filter - Paramètres de filtrage (geo, typesDeBiens, …)
 * @returns {{
 *   csvData: Object|null,
 *   colons: Object,
 *   loading: boolean,
 *   processing: boolean,
 *   tableReady: boolean,
 *   sourcesWithState: Array
 * }}
 */
export function useExtractData(filter) {

    // ── États de chargement ───────────────────────────────────────────────────
    const [loading,        setLoading]        = React.useState(false);
    const [loadingBdnbAll, setLoadingBdnbAll] = React.useState(-1);
    const [loadingDpes,    setLoadingDpes]    = React.useState(-1);
    const [loadingEtab,    setLoadingEtab]    = React.useState(-1);
    const [processing,     setProcessing]     = React.useState(false);
    const [tableReady,     setTableReady]     = React.useState(false);

    // ── Données brutes ────────────────────────────────────────────────────────
    const [bdnbAll, setBdnbAll] = React.useState({});
    const [dpes,    setDpes]    = React.useState([]);
    const [etab,    setEtab]    = React.useState([]);

    // ── Données traitées ──────────────────────────────────────────────────────
    const [csvData, setCsvData] = React.useState(null);
    const [colons,  setColons]  = React.useState({});

    // ── État de progression par source (pour le LoadingPanel) ─────────────────
    const sourcesWithState = SOURCES_CONFIG.map(s => ({
        ...s,
        value: {
            etab: loadingEtab, Dpes: loadingDpes,
            BdnbProp: loadingBdnbAll, bdnb: loadingBdnbAll,
            BdnbBatimentGroupeRnc: loadingBdnbAll, batimentGroupe: loadingBdnbAll,
            BatimentGroupeAdresses: loadingBdnbAll, Parcelles: loadingBdnbAll,
        }[s.key],
    }));

    // ── Pagination générique ──────────────────────────────────────────────────

    function applyPaginatedResults(results, limit, offset, setData, setLoadingFn, fetchNext) {
        setData(prev => [...prev, ...results]);
        if (!DEV_LIMIT && results.length === limit) { setLoadingFn(p => p + 1); fetchNext(limit, offset + limit); }
        else setLoadingFn(-1);
    }

    // ── Fonctions de chargement par source ────────────────────────────────────

    function getBdnbAll(l, o) {
        ExtractCtrl.bdnbAllPost(filter, l, o, result => {
            // Fusionner la page reçue : concaténer les tableaux, écraser les scalaires
            setBdnbAll(prev => {
                const merged = {...prev};
                Object.entries(result || {}).forEach(([key, val]) => {
                    merged[key] = Array.isArray(val)
                        ? [...(prev[key] || []), ...val]
                        : val;
                });
                return merged;
            });

            // Continuer tant qu'au moins une clé contient des éléments (désactivé en dev)
            const hasMore = !DEV_LIMIT && Object.values(result || {}).some(v => Array.isArray(v) && v.length > 0);
            if (hasMore) {
                setLoadingBdnbAll(p => p + 1);
                getBdnbAll(l, o + l);
            } else {
                setLoadingBdnbAll(-1);
            }
        });
    }

    function setEtabbData(r, l, o) { applyPaginatedResults(r, l, o, setEtab, setLoadingEtab, getEtab); }
    function getEtab(l, o)         { ExtractCtrl.getEtab(filter, l, o, r => setEtabbData(r, l, o)); }

    function setDpesData(r, l, o)  { applyPaginatedResults(r, l, o, setDpes, setLoadingDpes, getDpes); }
    function getDpes(l, o)         { ExtractCtrl.getDpes(filter, l, o, r => setDpesData(r, l, o)); }

    // ── Déclenchement initial de chaque source ────────────────────────────────

    React.useEffect(() => {
        if (!filter?.geo) return;
        setCsvData(null);
        setLoading(true);
        setBdnbAll({}); setEtab([]); setDpes([]);
        setLoadingBdnbAll(1); setLoadingEtab(1); setLoadingDpes(1);
    }, [filter]);

    // ── Fusion des données une fois toutes les sources terminées ──────────────

    React.useEffect(() => {
        const allDone = [loadingBdnbAll, loadingDpes, loadingEtab].every(v => v === -1);
        if (!allDone) {
            const limit = 100; // DEV_LIMIT bloque la pagination, pas la taille du lot
            if (loadingBdnbAll === 1) getBdnbAll(limit, 0);
            if (loadingDpes    === 1) getDpes(limit, 0);
            if (loadingEtab    === 1) getEtab(limit, 0);
            return;
        }

        setProcessing(true);
        const tid = setTimeout(() => {
            const batimentsList          = bdnbAll?.batiments                || [];
            const batimentGroupeList     = bdnbAll?.batiment_groupe          || [];
            const batimentGroupeRncList  = bdnbAll?.batiment_groupe_rnc      || [];
            const proprietairesList      = bdnbAll?.proprietaires            || [];
            const batimentGroupeAdresses = bdnbAll?.batiment_groupe_adresses || [];
            const parcellesList          = bdnbAll?.parcelles                || [];

            // ── 1. Construire la map principale batiment_groupe_id → sources ──
            const map = {};
            // ── 2. Objet lien : cle_interop_adr → premier batiment_groupe_id ─
            const addrToGroupId = {};

            expandGeom(batimentGroupeAdresses.map(a => ({...a, geom: a.geom_adresse})))
                .forEach(item => {
                    const bgId = item.batiment_groupe_id;
                    const addr = item.cle_interop_adr;
                    if (!bgId) return;
                    if (!map[bgId]) map[bgId] = {
                        batiments: [], batimentGroupe: [], batimentGroupeRnc: [],
                        proprietaires: [], batimentGroupeAdresses: [], parcelles: [],
                        dpes: [], etab: [],
                    };
                    map[bgId].batimentGroupeAdresses.push(item);
                    // Mémoriser le premier groupe associé à cette adresse
                    if (addr && !addrToGroupId[addr]) addrToGroupId[addr] = bgId;
                });

            // Bâtiments : lien via cle_interop_adr → addrToGroupId
            batimentsList.forEach(item => {
                const bgId = addrToGroupId[item.cle_interop_adr];
                if (bgId && map[bgId]) map[bgId].batiments.push(item);
            });

            // Groupes, RNC, propriétaires, parcelles : lien direct batiment_groupe_id
            const pushToGroup = (list, key) =>
                list.forEach(item => {
                    if (map[item.batiment_groupe_id]) map[item.batiment_groupe_id][key].push(item);
                });
            pushToGroup(batimentGroupeList,    'batimentGroupe');
            pushToGroup(batimentGroupeRncList, 'batimentGroupeRnc');
            pushToGroup(proprietairesList,     'proprietaires');
            pushToGroup(parcellesList,         'parcelles');

            // Calcul de la surface utile par groupe de bâtiments
            Object.keys(map).forEach(bgId => {
                const group = map[bgId];
                const annee = group.batimentGroupe[0]?.annee_construction;
                group.surface_utile = computeSurface(group.batiments, annee);
            });

            // DPEs et établissements : lien via adresse → addrToGroupId
            dpes.forEach(dpe => {
                const bgId = addrToGroupId[dpe.identifiant_ban];
                if (bgId && map[bgId]) map[bgId].dpes.push(dpe);
            });
            etab.forEach(item => {
                const bgId = addrToGroupId[item.cle_interop_adr];
                if (bgId && map[bgId]) map[bgId].etab.push(item);
            });

            const cols = {
                etab:                   buildColsMeta(etab),
                bdnb:                   buildColsMeta(batimentsList),
                batimentGroupe:         buildColsMeta(batimentGroupeList),
                BdnbProp:               buildColsMeta(proprietairesList),
                BdnbBatimentGroupeRnc:  buildColsMeta(batimentGroupeRncList),
                BatimentGroupeAdresses: buildColsMeta(batimentGroupeAdresses),
                Parcelles:              buildColsMeta(parcellesList),
                Dpes:                   buildColsMeta(dpes),
            };

            setColons(cols);
            if (Object.keys(map).length > 0) {
                setTableReady(false);
                setCsvData(map);
            }
            setLoading(false);
            setProcessing(false);
        }, 30);

        return () => clearTimeout(tid);
    }, [loadingEtab, loadingDpes, loadingBdnbAll]);

    // ── Délai minimal avant d'afficher le tableau (évite le freeze UI) ────────

    React.useEffect(() => {
        if (!csvData) { setTableReady(false); return; }
        const t = setTimeout(() => setTableReady(true), 50);
        return () => clearTimeout(t);
    }, [csvData]);

    return {csvData, colons, loading, processing, tableReady, sourcesWithState};
}

